import React, { useCallback } from 'react';
import { Button, Typography } from '@duxton-web/ui';

import DefaultRule from './default-rule';
import RuleCard, { DEFAULT_RULE_CONDITIONS } from './rule-card';
import {
  useVariableContext,
  useVariableDispatchContext,
} from '@/features/variable/contexts/variable-context';
import {
  variableDetailUpdate,
  variableJsonSchemaUpdate,
} from '@/features/variable/store/actions/variable/action-creators';
import {
  Constraint,
  ContextualCondition,
  ContextualRule,
  VariableInfo,
} from '@/lib/PlannerDTOs';

const EMPTY_DEFAULT_RULE: ContextualRule = { contextualRulesConditions: [] };

const MAX_RULES = 10;

const toContextualCondition = (
  condition: Partial<Constraint>,
): ContextualCondition => {
  return {
    target: condition?.target || '',
    op: condition?.op || '',
    value: condition?.value || '',
  };
};

const isDefaultRule = (rule: ContextualRule) =>
  !rule?.contextualRulesConditions?.length;

const Contextual = () => {
  const { variable, disabled, variableIsValid, jsonSchemaIsValid } =
    useVariableContext();
  const dispatch = useVariableDispatchContext();
  const allRules = variable?.metadata?.contextualRules || [];

  const regularRules = allRules.filter((rule) => !isDefaultRule(rule));
  const defaultRule =
    allRules.find((rule) => isDefaultRule(rule)) ?? EMPTY_DEFAULT_RULE;

  const updateContextualRules = useCallback(
    (nextRules: ContextualRule[]) => {
      const hasRules = nextRules.some(
        (rule) =>
          rule?.contextualRulesConditions?.length > 0 ||
          rule?.jsonSchema != null ||
          rule?.allowedValues != null ||
          rule?.minimumLength != null ||
          rule?.maximumLength != null ||
          rule?.minimumValue != null ||
          rule?.maximumValue != null,
      );
      const shouldClearJsonSchema = hasRules || !jsonSchemaIsValid;

      const updatedVariable = {
        ...variable,
        metadata: {
          ...variable?.metadata,
          contextualRules: nextRules,
          ...(shouldClearJsonSchema ? { jsonSchema: null } : {}),
        },
      } as VariableInfo;

      if (shouldClearJsonSchema) {
        dispatch(variableJsonSchemaUpdate(null, true));
      }
      dispatch(variableDetailUpdate(updatedVariable, variableIsValid));
    },
    [variable, variableIsValid, jsonSchemaIsValid, dispatch],
  );

  const handleAddRule = () => {
    updateContextualRules([
      ...regularRules,
      {
        contextualRulesConditions: DEFAULT_RULE_CONDITIONS.map(
          toContextualCondition,
        ),
      },
      defaultRule,
    ]);
  };

  const handleDeleteRule = (ruleIndexToDelete: number) => {
    updateContextualRules([
      ...regularRules.filter((_, i) => i !== ruleIndexToDelete),
      defaultRule,
    ]);
  };

  const handleRuleChange = useCallback(
    (ruleIndex: number, nextRule: ContextualRule) => {
      updateContextualRules([
        ...regularRules.map((rule, index) =>
          index === ruleIndex ? nextRule : rule,
        ),
        defaultRule,
      ]);
    },
    [regularRules, defaultRule, updateContextualRules],
  );

  const handleDefaultRuleChange = useCallback(
    (nextRule: ContextualRule) => {
      updateContextualRules([...regularRules, nextRule]);
    },
    [regularRules, updateContextualRules],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Typography.Metadata>Contextual Rules</Typography.Metadata>
      {regularRules.map((rule, index) => {
        return (
          <RuleCard
            key={`contextual-rule-${index}`}
            title={`Rule ${index + 1}`}
            ruleIndex={index}
            rule={rule}
            disabled={disabled}
            onDelete={() => handleDeleteRule(index)}
            onRuleChange={handleRuleChange}
          />
        );
      })}

      <Button
        size="small"
        disabled={disabled || regularRules.length >= MAX_RULES - 1}
        onClick={handleAddRule}
      >
        Add Rule
      </Button>

      <DefaultRule rule={defaultRule} onRuleChange={handleDefaultRuleChange} />
    </div>
  );
};

export default Contextual;
